/**
 * The drawer's sticky note composer footer. Collapsed at rest (a "View
 * notes (N)" + "Add note" row); expands to a multiline textarea with a
 * posting-identity row, FP-visibility heads-up, char counter and submit.
 * Consumes the {@link DrawerNotes} bundle from ``useDrawerNotes`` - it owns
 * no state of its own.
 */
import React from 'react'
import {
  Avatar,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { AppDrawerFooter } from '../../../shared/AppDrawer'
import RoleBadge from '../../../shared/RoleBadge'
import { initialsOf } from '../../../../utils/userDisplay'
import { poolwerxColors } from '../../../../theme/colors'
import {
  WARNING_BG,
  WARNING_FG,
} from '../../../../config/exceptions/shared/issuesState'
import { BODY_MAX, type DrawerNotes } from './useDrawerNotes'

interface ComposerUser {
  name?: string | null
  email?: string | null
  role?: string | null
}

interface NotesComposerProps {
  notes: DrawerNotes
  user: ComposerUser | null | undefined
}

const NotesComposer: React.FC<NotesComposerProps> = ({ notes, user }) => {
  const {
    draft,
    setDraft,
    setComposerOpen,
    posting,
    composerFlash,
    composerInputRef,
    charCount,
    overLimit,
    warnCount,
    linkError,
    canPost,
    handlePost,
  } = notes
  return (
    <AppDrawerFooter
      padding={{
        px: 2,
        // Extra vertical breathing room around the "Posting as" row and the
        // input so the footer doesn't read as crammed under the scroll area.
        pt: 1.75,
        pb: 1.5,
      }}
    >
      <Box role="group" aria-label="Notes composer">
        {/* Always-open composer - the Notes tab footer. Posting identity + a
          * quiet visibility heads-up sit above the input. */}
        {user && (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1}
                sx={{ mb: 1.25 }}
              >
                <Stack
                  direction="row"
                  spacing={0.75}
                  alignItems="center"
                  sx={{ minWidth: 0 }}
                >
                  <Avatar
                    sx={{
                      width: 18,
                      height: 18,
                      fontSize: '0.55rem',
                      bgcolor: poolwerxColors.teal,
                    }}
                  >
                    {initialsOf(user.name, user.email)}
                  </Avatar>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={{ fontWeight: 500, fontSize: '0.72rem', lineHeight: 1.2 }}
                  >
                    Posting as {user.name || user.email}
                  </Typography>
                  <Box
                    component="span"
                    sx={{ color: 'text.disabled', fontSize: '0.72rem' }}
                  >
                    ·
                  </Box>
                  <RoleBadge user={user} />
                </Stack>
                {/* Amber caution chip - notes are FP-visible, so this reads as
                  * a warning (not a quiet grey aside) before the user posts. */}
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  sx={{
                    flexShrink: 0,
                    px: 0.875,
                    py: 0.375,
                    borderRadius: 1,
                    bgcolor: WARNING_BG,
                    border: `1px solid ${WARNING_FG}40`,
                    color: poolwerxColors.text.primary,
                  }}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 13, color: WARNING_FG }} />
                  <Typography
                    variant="caption"
                    sx={{ fontSize: '0.68rem', lineHeight: 1.2, fontWeight: 500 }}
                  >
                    Visible to Franchise Partners
                  </Typography>
                </Stack>
              </Stack>
            )}
            <TextField
              fullWidth
              multiline
              inputRef={composerInputRef}
              minRows={3}
              maxRows={8}
              placeholder="Add a note..."
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                  e.preventDefault()
                  handlePost()
                }
              }}
              inputProps={{ 'aria-label': 'New note body' }}
              error={overLimit || Boolean(linkError)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                  fontSize: '14px',
                  lineHeight: '20px',
                  py: 0.75,
                  px: 1.25,
                  transition: 'box-shadow 200ms ease',
                  // Brief teal ring after "Add note" so the jump to this input
                  // is obvious; fades out when composerFlash clears.
                  ...(composerFlash
                    ? {
                        boxShadow: `0 0 0 3px ${poolwerxColors.teal}55`,
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: poolwerxColors.teal,
                        },
                      }
                    : {}),
                },
                '& .MuiOutlinedInput-input': {
                  fontSize: '14px',
                  lineHeight: '20px',
                  padding: 0,
                },
                '& .MuiOutlinedInput-input::placeholder': {
                  fontSize: '14px',
                  fontWeight: 400,
                  color: 'text.secondary',
                  opacity: 0.7,
                },
              }}
            />
            {linkError && (
              <Typography
                variant="caption"
                sx={{
                  mt: 0.5,
                  display: 'block',
                  color: 'error.main',
                  fontSize: '0.7rem',
                }}
              >
                {linkError}
              </Typography>
            )}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mt: 0.75, minHeight: 24 }}
            >
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.68rem',
                    color: 'text.disabled',
                    display: { xs: 'none', sm: 'inline' },
                  }}
                >
                  Ctrl+Enter to post
                </Typography>
                {(warnCount || overLimit) && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.68rem',
                      color: overLimit ? 'error.main' : 'warning.main',
                    }}
                  >
                    {charCount} / {BODY_MAX}
                  </Typography>
                )}
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  size="small"
                  onClick={() => {
                    setComposerOpen(false)
                    setDraft('')
                  }}
                  disabled={posting}
                  sx={{ textTransform: 'none', color: 'text.secondary' }}
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handlePost}
                  disabled={
                    !draft.trim() ||
                    overLimit ||
                    posting ||
                    !canPost ||
                    Boolean(linkError)
                  }
                  sx={{
                    bgcolor: poolwerxColors.teal,
                    '&:hover': { bgcolor: poolwerxColors.tealHover },
                    textTransform: 'none',
                    px: 2.25,
                    py: 0.5,
                    fontSize: '0.82rem',
                    lineHeight: 1.4,
                  }}
                >
                  {posting ? 'Adding...' : 'Add note'}
                </Button>
              </Stack>
            </Stack>
      </Box>
    </AppDrawerFooter>
  )
}

export default NotesComposer
